import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A cheerful garden birdhouse on a wooden post: plump teal walls under an
// oversized cream roof, a round charcoal entrance hole with a stubby perch
// peg — and a tiny round bird living inside. Built to be watched: the whole
// house sways gently on its post ("idle"), and the bird pops out of the hole,
// looks left and right, and ducks back in on a loop ("peek"). Storybook
// batch: plump masses, oversized roof and peg, warm saturated colours +
// cream trim.
//
// Every fixed part shares the name "house" so the sway drives post, box and
// roof as one node; the bird is its own node so the peek can move it alone.
//
// Frame: y = 0 is the ground, +Z is the front (the hole faces forward).
// Roughly 1.4 m tall over a 1x1 tile.

const TEAL = "#2fa39b";        // house walls
const TEAL_DARK = "#22766f";   // wall shading (base plate, hole rim)
const CREAM = "#f6efe3";       // roof + trim
const WOOD = "#a06b3a";        // post + perch peg
const WOOD_DARK = "#7c4f27";   // post shading (foot mound)
const HOLE = "#2c2f36";        // deep charcoal entrance
const BIRD = "#ff8a4d";        // the little round tenant
const BEAK = "#ffd23f";        // its stubby beak

// ── frame ────────────────────────────────────────────────────────────────────
const POST_H = 0.88;                  // post top (house floor sits here)
const BOX_W = 0.44, BOX_H = 0.38, BOX_D = 0.36;
const BOX_CY = POST_H + BOX_H / 2;    // house box centre (~1.07)
const HOLE_Y = BOX_CY + 0.03;         // entrance hole height
const FRONT_Z = BOX_D / 2;            // front wall plane
const ROOF_Y = POST_H + BOX_H;        // eaves height (~1.26), ridge ~1.42

// The sway rocks everything about the post's foot; the peek turns the bird
// about its own centre at the hole.
const FOOT_PIVOT: [number, number, number] = [0, 0, 0];
const BIRD_POS: [number, number, number] = [0, HOLE_Y, FRONT_Z + 0.025];

// A tiny sway applied identically to house and bird, so the bird rides the
// house instead of hovering while the walls lean away from it.
function swayTrack(target: string, dur: number, lean: number) {
  return {
    target,
    pivot: FOOT_PIVOT,
    rotation: [
      { t: 0, value: [0, 0, lean] as [number, number, number], ease: "sine" as const },
      { t: dur / 2, value: [0, 0, -lean] as [number, number, number], ease: "sine" as const },
      { t: dur, value: [0, 0, lean] as [number, number, number], ease: "sine" as const },
    ],
  };
}

export const birdhouse = defineModel({
  id: "birdhouse",
  batchId: "mess",
  name: "Birdhouse",
  tags: ["mess", "cute", "animated", "prop", "garden", "bird", "home"],
  summary: "A teal birdhouse on a post whose tiny round bird peeks out of the hole.",
  description:
    "A cheerful toy-like birdhouse: plump teal walls under an oversized cream " +
    "roof on a chunky wooden post, with a round charcoal entrance hole and a " +
    "stubby perch peg. A tiny round orange bird lives inside — the idle " +
    "animation sways the whole house gently on its post, and the peek " +
    "animation pops the bird out of the hole to look left and right before " +
    "ducking back in.",
  author: "models",
  footprint: 0.35,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // Post: a grassy foot mound, a chunky pole and a cream collar under the
      // floor so the house reads seated, not skewered.
      { geometry: new THREE.SphereGeometry(0.13, 10, 7), color: WOOD_DARK,
        position: [0, 0.02, 0], scale: [1, 0.45, 1], name: "house" },
      { geometry: new THREE.CylinderGeometry(0.05, 0.065, POST_H, 10), color: WOOD,
        position: [0, POST_H / 2, 0], name: "house" },
      { geometry: new THREE.CylinderGeometry(0.085, 0.06, 0.07, 10), color: CREAM,
        position: [0, POST_H - 0.03, 0], name: "house" },

      // The house box, slightly plumped, over a fat teal base plate.
      { geometry: new THREE.BoxGeometry(BOX_W + 0.06, 0.06, BOX_D + 0.06), color: TEAL_DARK,
        position: [0, POST_H + 0.03, 0], name: "house" },
      { geometry: new THREE.BoxGeometry(BOX_W, BOX_H, BOX_D), color: TEAL,
        position: [0, BOX_CY, 0], name: "house" },

      // Oversized cream roof: two pitched slabs meeting at a ridge cap.
      { geometry: new THREE.BoxGeometry(0.32, 0.045, BOX_D + 0.14), color: CREAM,
        position: [-0.135, ROOF_Y + 0.085, 0], rotation: [0, 0, 0.62], name: "house" },
      { geometry: new THREE.BoxGeometry(0.32, 0.045, BOX_D + 0.14), color: CREAM,
        position: [0.135, ROOF_Y + 0.085, 0], rotation: [0, 0, -0.62], name: "house" },
      { geometry: new THREE.CylinderGeometry(0.035, 0.035, BOX_D + 0.16, 8), color: TEAL_DARK,
        position: [0, ROOF_Y + 0.165, 0], rotation: [Math.PI / 2, 0, 0], name: "house" },

      // The entrance: a cream ring around a deep charcoal hole, with a stubby
      // wooden perch peg beneath it.
      { geometry: new THREE.CylinderGeometry(0.105, 0.105, 0.025, 14), color: CREAM,
        position: [0, HOLE_Y, FRONT_Z + 0.005], rotation: [Math.PI / 2, 0, 0], name: "house" },
      { geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.04, 14), color: HOLE,
        position: [0, HOLE_Y, FRONT_Z + 0.008], rotation: [Math.PI / 2, 0, 0], name: "house" },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.14, 8), color: WOOD,
        position: [0, HOLE_Y - 0.13, FRONT_Z + 0.05], rotation: [Math.PI / 2, 0, 0], name: "house" },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: WOOD_DARK,
        position: [0, HOLE_Y - 0.13, FRONT_Z + 0.12], name: "house" },

      // The tenant: a tiny round bird poking out of the hole — a plump body,
      // a stubby beak and a cream chest patch, all sharing the name "bird".
      { geometry: new THREE.SphereGeometry(0.062, 10, 8), color: BIRD,
        position: BIRD_POS, name: "bird" },
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: CREAM,
        position: [0, HOLE_Y - 0.02, FRONT_Z + 0.07], name: "bird" },
      { geometry: new THREE.ConeGeometry(0.02, 0.05, 6), color: BEAK,
        position: [0, HOLE_Y + 0.015, FRONT_Z + 0.095], rotation: [Math.PI / 2, 0, 0], name: "bird" },
    ];
  },
  animations: [
    // 1. Idle — the whole house sways gently on its post in a soft breeze;
    //    the bird track carries the same lean so it rides along.
    {
      id: "idle",
      label: "Sway",
      duration: 3.4,
      loop: true,
      tracks: [swayTrack("house", 3.4, 0.03), swayTrack("bird", 3.4, 0.03)],
    },
    // 2. Peek — the signature move: the bird pops out of the hole with a
    //    little bounce, looks left, looks right, and ducks back inside.
    {
      id: "peek",
      label: "Bird peek",
      duration: 4.2,
      loop: true,
      tracks: [
        {
          target: "bird",
          pivot: BIRD_POS,
          position: [
            { t: 0, value: [0, 0, -0.18] },              // hidden inside
            { t: 0.5, value: [0, 0, -0.18] },            // ...waiting
            { t: 0.9, value: [0, 0.015, 0.02], ease: "bounce" }, // pop!
            { t: 3.4, value: [0, 0.015, 0.02] },         // holding, looking
            { t: 3.8, value: [0, 0, -0.18], ease: "easeIn" },    // duck back in
            { t: 4.2, value: [0, 0, -0.18] },
          ],
          rotation: [
            { t: 0.9, value: [0, 0, 0] },
            { t: 1.5, value: [0, 0.55, 0], ease: "easeInOut" },  // look left
            { t: 2.3, value: [0, -0.55, 0], ease: "easeInOut" }, // look right
            { t: 3.0, value: [0, 0, 0.12], ease: "easeInOut" },  // curious tilt
            { t: 3.4, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
